import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { SG_REWARDS_TERMS_AND_CONDITIONS } from '../../constants';
import { useLoyaltyPremiumStatus } from '../useLoyaltyPremiumStatus';
import { useLoyaltyContentFallbacks } from './hooks';
import { getUnlockableRewardsSubtitle } from './utils';

import type { Asset, Entry } from 'contentful';

/**
 * Returns the contentful data for the bag.
 */
export const useLoyaltyContent = (params?: UseLoyaltyContentParams) => {
  const { availablePoints = 0, nextReward } = params ?? {};
  const { isPremium } = useLoyaltyPremiumStatus();

  const content = useLoyaltyContentData();
  const fallbacks = useLoyaltyContentFallbacks(availablePoints);

  return useMemo(() => {
    const isLoadingContent = content?.isLoadingContent;

    // ─── FAQ ────────────────────────────────────────────────────────────────

    const faqTitleXs = content?.faqTitleXs ?? fallbacks?.faqTitleXs;
    const faqSubtitleXs = content?.faqSubtitleXs ?? fallbacks?.faqSubtitleXs;
    const faqTitleSm = content?.faqTitleSm ?? fallbacks?.faqTitleSm;
    const faqSubtitleSm = content?.faqSubtitleSm ?? fallbacks?.faqSubtitleSm;

    const faqLoyaltyHomeContentfulData = isPremium
      ? content?.faqSgRewardsPremiumLoyaltyHome
      : content?.faqLoyaltyHome;

    const faqLoyaltyHome = (faqLoyaltyHomeContentfulData ?? EMPTY_FAQ)
      ?.map(extractLoyaltyFAQ)
      ?.filter(filterOutInvalidLoyaltyFAQ);

    const faqPointHistory = (content?.faqPointHistory ?? EMPTY_FAQ)
      ?.map(extractLoyaltyFAQ)
      ?.filter(filterOutInvalidLoyaltyFAQ);

    // ─── Bag ────────────────────────────────────────────────────────────────

    const bagHeaderLoggedOut = content?.bagHeaderLoggedOut ?? fallbacks?.bagHeaderLoggedOut;
    const bagHeaderNewUser = content?.bagHeaderNewUser ?? fallbacks?.bagHeaderNewUser;
    const bagHeaderRecurringUser =
      content?.bagHeaderRecurringUser ?? fallbacks?.bagHeaderRecurringUser;
    const bagHeaderSubtitle = content?.bagHeaderSubtitle ?? fallbacks?.bagHeaderSubtitle;
    const bagHeaderLoggedOutImageMessage =
      content?.bagLoggedOutImageMessage ?? fallbacks?.bagHeaderLoggedOutImageMessage;
    const bagHeaderLoggedOutImage = content?.bagHeaderLoggedOutImage?.fields?.file?.url;
    const bagDiscountBody = content?.bagDiscountBody ?? fallbacks?.bagDiscountBody;
    const orderStatusRewardDiscountNote =
      content?.orderStatusRewardDiscountNote ?? fallbacks.orderStatusRewardDiscountNote;

    // ─── Home Empty State ───────────────────────────────────────────────────

    const homeChallengesEmptyStateTitle =
      content?.homeChallengesEmptyStateTitle ?? fallbacks?.homeChallengesEmptyStateTitle;

    const homeChallengesEmptyStateBody =
      content?.homeChallengesEmptyStateBody ?? fallbacks?.homeChallengesEmptyStateBody;

    const homeChallengesEmptyStateCtaXS =
      content?.homeChallengesEmptyStateCtaXS ?? fallbacks?.homeChallengesEmptyStateCtaXS;

    const homeChallengesEmptyStateCtaSM =
      content?.homeChallengesEmptyStateCtaSM ?? fallbacks?.homeChallengesEmptyStateCtaSM;

    const homeRewardsEmptyStateTitle =
      content?.homeRewardsEmptyStateTitle ?? fallbacks?.homeRewardsEmptyStateTitle;

    const homeRewardsEmptyStateBody =
      content?.homeRewardsEmptyStateBody ?? fallbacks?.homeRewardsEmptyStateBody;

    // ─── Progress Benefits ──────────────────────────────────────────────────

    const homeProgressBenefits = (content?.homeProgressBenefits ?? EMPTY_HOME_PROGRESS_BENEFITS)
      .map(extractHomeProgressBenefit)
      .filter(filterOutInvalidHomeProgressBenefit);

    const homeProgressBenefitsDisclaimer = content?.homeProgressBenefitsDisclaimer;

    // ─── How It Works ───────────────────────────────────────────────────────

    const homeHowItWorksSMData = (content?.homeHowItWorksSM ?? EMPTY_HOW_IT_WORKS).map(
      extractLoyaltyHowItWorks,
    );

    const homeHowItWorksSM = (
      homeHowItWorksSMData.length > 0
        ? homeHowItWorksSMData
        : [
            {
              title: fallbacks.earnPointsTitle,
              description: fallbacks.earnPointsDescription,
            },
            {
              title: fallbacks.redeemRewardsTitle,
              description: fallbacks.redeemRewardsDescription,
            },
            {
              title: fallbacks.lookOutForOffersTitle,
              description: fallbacks.lookOutForOffersDescription,
            },
          ]
    ).filter(filterOutInvalidLoyaltyHowItWorks);

    const homeHowItWorksDisclaimer = content?.homeHowItWorksDisclaimer;

    // ─── Terms And Conditions ───────────────────────────────────────────────

    const termsAndConditionsUrl = content?.termsAndConditionsUrl ?? SG_REWARDS_TERMS_AND_CONDITIONS;

    // ─── Return Hero ─────────────────────────────────────────────────────────

    const returnHeroTitle = content?.returnHeroTitle ?? fallbacks?.returnHeroTitle;
    const returnHeroImage = content?.returnHeroImage?.fields?.file?.url;
    const returnHeroImageAlt = content?.returnHeroImage?.fields?.description;

    // ─── Loyalty Benefits ────────────────────────────────────────────────────

    const loyaltyBenefitsTitle = content?.loyaltyBenefitsTitle ?? fallbacks?.loyaltyBenefitsTitle;
    const loyaltyBenefitsSubtitle = content?.loyaltyBenefitsSubtitle ?? fallbacks?.loyaltyBenefitsSubtitle; // prettier-ignore

    // ─── Unlockable Rewards ──────────────────────────────────────────────────

    const unlockableRewardsTitle = content?.unlockableRewardsTitle ?? fallbacks?.unlockableRewardsTitle; // prettier-ignore
    const unlockableRewardsSubtitleNoPoints = content?.unlockableRewardsSubtitleNoPoints ?? fallbacks?.unlockableRewardsSubtitleNoPoints; // prettier-ignore
    const unlockableRewardsSubtitleDefault = content?.unlockableRewardsSubtitleDefault ?? fallbacks?.unlockableRewardsSubtitleDefault; // prettier-ignore
    const unlockableRewardsSubtitleTooManyPoints = content?.unlockableRewardsSubtitleTooManyPoints ?? fallbacks?.unlockableRewardsSubtitleTooManyPoints; // prettier-ignore
    const unlockableRewardsSubtitle = getUnlockableRewardsSubtitle({
      availablePoints,
      nextReward,
      unlockableRewardsSubtitleNoPoints,
      unlockableRewardsSubtitleDefault,
      unlockableRewardsSubtitleTooManyPoints,
    });

    return {
      isLoadingContent,
      faqTitleXs,
      faqSubtitleXs,
      faqTitleSm,
      faqSubtitleSm,
      faqLoyaltyHome,
      faqPointHistory,
      bagHeaderLoggedOut,
      bagHeaderNewUser,
      bagHeaderRecurringUser,
      bagHeaderSubtitle,
      bagHeaderLoggedOutImageMessage,
      bagHeaderLoggedOutImage,
      bagDiscountBody,
      returnHeroTitle,
      returnHeroImage,
      returnHeroImageAlt,
      homeProgressBenefits,
      homeProgressBenefitsDisclaimer,
      homeHowItWorksSM,
      homeHowItWorksDisclaimer,
      homeChallengesEmptyStateTitle,
      homeChallengesEmptyStateBody,
      homeChallengesEmptyStateCtaXS,
      homeChallengesEmptyStateCtaSM,
      homeRewardsEmptyStateTitle,
      homeRewardsEmptyStateBody,
      termsAndConditionsUrl,
      orderStatusRewardDiscountNote,
      loyaltyBenefitsTitle,
      loyaltyBenefitsSubtitle,
      unlockableRewardsTitle,
      unlockableRewardsSubtitle,
    };
  }, [content, fallbacks, isPremium, nextReward, availablePoints]);
};

// ─── Container ──────────────────────────────────────────────────────────────

type LoyaltyContainer = {
  isLoadingContent: boolean;

  faqTitleXs: string;
  faqSubtitleXs: string;
  faqTitleSm: string;
  faqSubtitleSm: string;
  faqLoyaltyHome: Entry<LoyaltyFAQ>[];
  faqSgRewardsPremiumLoyaltyHome: Entry<LoyaltyFAQ>[];
  faqPointHistory: Entry<LoyaltyFAQ>[];

  bagHeaderLoggedOut?: string;
  bagHeaderNewUser?: string;
  bagHeaderRecurringUser?: string;
  bagHeaderSubtitle?: string;
  bagLoggedOutImageMessage?: string;
  bagHeaderLoggedOutImage?: Asset;
  bagDiscountBody?: string;

  homeProgressBenefits: Entry<ProgressBenefit>[];
  homeProgressBenefitsDisclaimer: string;

  homeHowItWorksSM: Entry<LoyaltyHowItWorks>[];
  homeHowItWorksDisclaimer: string;

  homeChallengesEmptyStateTitle: string;
  homeChallengesEmptyStateBody: string;
  homeChallengesEmptyStateCtaXS: string;
  homeChallengesEmptyStateCtaSM: string;

  returnHeroTitle: string;
  returnHeroImage: Asset;

  homeRewardsEmptyStateTitle: string;
  homeRewardsEmptyStateBody: string;

  loyaltyBenefitsTitle: string;
  loyaltyBenefitsSubtitle: string;

  unlockableRewardsTitle: string;
  unlockableRewardsSubtitleNoPoints: string;
  unlockableRewardsSubtitleDefault: string;
  unlockableRewardsSubtitleTooManyPoints: string;

  termsAndConditionsUrl?: string;

  orderStatusRewardDiscountNote: string;
};

// ─── Loyalty FAQ ────────────────────────────────────────────────────────────

type LoyaltyFAQ = {
  title?: string;
  description?: string;
};

const extractLoyaltyFAQ = (loyaltyFAQ?: Entry<LoyaltyFAQ>) => {
  return {
    title: loyaltyFAQ?.fields?.title,
    description: loyaltyFAQ?.fields?.description,
  };
};

const filterOutInvalidLoyaltyFAQ = (entry: Partial<LoyaltyFAQ>): entry is Required<LoyaltyFAQ> => {
  return Boolean(entry.title && entry.description);
};

const EMPTY_FAQ: Entry<LoyaltyFAQ>[] = [];

// ─── Loyalty How It Works ───────────────────────────────────────────────────

type LoyaltyHowItWorks = {
  title?: string;
  description?: string;
  image?: Asset;
};

type LoyaltyHowItWorksPostExtraction = {
  title?: string;
  description?: string;
  image?: string;
};

const extractLoyaltyHowItWorks = (
  loyaltyHowItWorks?: Entry<LoyaltyHowItWorks>,
): LoyaltyHowItWorksPostExtraction => {
  return {
    title: loyaltyHowItWorks?.fields?.title,
    description: loyaltyHowItWorks?.fields?.description,
    image: loyaltyHowItWorks?.fields?.image?.fields?.file?.url,
  };
};

const filterOutInvalidLoyaltyHowItWorks = (
  entry: Partial<LoyaltyHowItWorksPostExtraction>,
): entry is Required<LoyaltyHowItWorksPostExtraction> => {
  return Boolean(entry.title && entry.description);
};

const EMPTY_HOW_IT_WORKS: Entry<LoyaltyHowItWorks>[] = [];

// ─── Progress Benefits ──────────────────────────────────────────────────────

type ProgressBenefit = {
  title?: string;
  description?: string;
};

export type MappedProgressBenefit = {
  title: string;
  maxPoints: number;
  isPendingBenefit: boolean;
};

const extractHomeProgressBenefit = (
  benefit?: Entry<ProgressBenefit>,
): Partial<MappedProgressBenefit> => {
  return {
    title: benefit?.fields?.title,
    maxPoints: Number(benefit?.fields?.description),
    isPendingBenefit: benefit?.fields?.description === 'Pending',
  };
};

const filterOutInvalidHomeProgressBenefit = (
  entry: Partial<MappedProgressBenefit>,
): entry is Required<MappedProgressBenefit> => {
  return (
    entry.title !== undefined &&
    (Number.isInteger(entry.maxPoints) || entry.isPendingBenefit === true) &&
    entry.isPendingBenefit !== undefined
  );
};

const EMPTY_HOME_PROGRESS_BENEFITS: Entry<ProgressBenefit>[] = [];

// ─── Content Joining ────────────────────────────────────────────────────────

const useLoyaltyContentData = () => {
  const contentfulResponse1 = useContentfulContentTypeEntry<LoyaltyContainer>({ contentType: 'loyalty' }); // prettier-ignore
  const contentfulResponse2 = useContentfulContentTypeEntry<LoyaltyContainer>({ contentType: 'loyalty2' }); // prettier-ignore

  return useMemo(() => {
    const isLoadingContent = contentfulResponse1.fetching || contentfulResponse2.fetching;
    const content1 = contentfulResponse1.data?.fields ?? {};
    const content2 = contentfulResponse2.data?.fields ?? {};

    return { isLoadingContent, ...content1, ...content2 } as LoyaltyContainer;
  }, [
    contentfulResponse1.data?.fields,
    contentfulResponse2.data?.fields,
    contentfulResponse1.fetching,
    contentfulResponse2.fetching,
  ]);
};

// ─── Params ─────────────────────────────────────────────────────────────────

type UseLoyaltyContentParams = {
  availablePoints: number | undefined;
  nextReward?: { name: string; pointsLeft: number };
};
